import '../i18n/autoTranslate';

// CodeEditor.jsx (CodeMirror 6)
// Keeps your UI style, upgrades the editor + workflow.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Play,
  Square,
  RotateCcw,
  Wand2,
  AlertCircle,
  Sparkles,
  Loader2,
  Code2,
  ClipboardList,
  NotebookText,
} from "lucide-react";

import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView, keymap } from "@codemirror/view";
import { indentWithTab } from "@codemirror/commands";

const TABS = [
  { id: "solution", label: "Solution", Icon: Code2 },
  { id: "tests", label: "Tests", Icon: ClipboardList },
  { id: "notes", label: "Notes", Icon: NotebookText },
];

function msToClock(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function safeLineCol(view) {
  try {
    const pos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(pos);
    const col = pos - line.from;
    return { line: line.number, col: col + 1 };
  } catch {
    return { line: 1, col: 1 };
  }
}

export default function CodeEditor({
  code,
  tests,
  notes,
  onChangeCode,
  onChangeTests,
  onChangeNotes,
  language,
  setLanguage,
  onRun,
  onSubmit,
  onReset,
  onFormat,
  running = false,
  runningMs = 0,
  lastRun = null, // { compile_ok, run_ok, stdout, stderr, duration_ms, tests: [...] }
  readOnly = false,
  onCheckpoint = null, // optional: called with {tab, code, tests, notes, language}
}) {
  const [tab, setTab] = useState("solution");
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const cmRef = useRef(null);

  // Keyboard shortcuts (VS Code-ish)
  useEffect(() => {
    const handler = (e) => {
      if (readOnly) return;
      const k = String(e.key || "").toLowerCase();

      // Ctrl/Cmd+S => checkpoint
      if ((e.ctrlKey || e.metaKey) && k === "s") {
        e.preventDefault();
        onCheckpoint?.({ tab, code, tests, notes, language });
        return;
      }

      // Ctrl/Cmd+Enter => Run
      if ((e.ctrlKey || e.metaKey) && k === "enter") {
        e.preventDefault();
        if (tab !== "notes" && !running) onRun?.();
        return;
      }

      // Ctrl/Cmd+Shift+Enter => Submit
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && k === "enter") {
        e.preventDefault();
        if (tab !== "notes" && !running) onSubmit?.();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [readOnly, tab, code, tests, notes, language, running, onRun, onSubmit, onCheckpoint]);

  const isTooLong = useMemo(() => {
    const active = tab === "solution" ? code : tab === "tests" ? tests : notes;
    const lineCount = String(active || "").split("\n").length;
    return lineCount > 250;
  }, [tab, code, tests, notes]);

  const extensions = useMemo(() => {
    if (tab === "notes") {
      return [
        oneDark,
        EditorView.lineWrapping,
        keymap.of([indentWithTab]),
        EditorView.theme(
          {
            "&": { fontSize: "13px" },
            ".cm-content": {
              fontFamily:
                "'Fira Code', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              padding: "1.25rem",
            },
            ".cm-gutters": { paddingLeft: "0.25rem" },
          },
          { dark: true }
        ),
      ];
    }

    const langExt =
      language === "python"
        ? python()
        : language === "typescript"
        ? javascript({ typescript: true, jsx: false })
        : javascript({ typescript: false, jsx: true });

    return [
      oneDark,
      langExt,
      keymap.of([indentWithTab]),
      EditorView.lineWrapping,
      EditorView.theme(
        {
          "&": { fontSize: "13px" },
          ".cm-content": {
            fontFamily:
              "'Fira Code', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            padding: "1.25rem",
          },
          ".cm-gutters": { paddingLeft: "0.25rem" },
          ".cm-lineNumbers": { color: "rgba(255,255,255,0.40)" },
        },
        { dark: true }
      ),
    ];
  }, [language, tab]);

  const activeValue = tab === "solution" ? code : tab === "tests" ? tests : notes;
  const onChangeActive = (val) => {
    if (tab === "solution") onChangeCode(val);
    else if (tab === "tests") onChangeTests(val);
    else onChangeNotes(val);
  };

  // Autosave / checkpoint hook
  useEffect(() => {
    if (!onCheckpoint) return;
    const t = setTimeout(() => {
      onCheckpoint({ tab, code, tests, notes, language });
    }, 2500);
    return () => clearTimeout(t);
  }, [tab, code, tests, notes, language, onCheckpoint]);

  return (
    <div className="flex flex-col h-full overflow-hidden relative">
      {/* Header */}
      <div className="overflow-hidden md:overflow-x-auto px-5 py-4 border-b border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent flex items-center justify-between gap-4">
        <div className="flex items-center justify-around gap-1 w-full">
          <div className="absolute top-2 left-2 flex items-center gap-1.5 shrink-0">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-300/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-200/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-200/70" />
          </div>

          <div className="h-5 w-px bg-white/12" />

          <div className="text-xs text-white/65 font-semibold tracking-widest uppercase flex items-center gap-2 shrink-0">
            <Sparkles className="h-4 w-4 opacity-70" />
            Editor
          </div>

          <div className="h-5 w-px bg-white/12" />

          {/* Tabs */}
          <div className="flex items-center gap-1 rounded-2xl border border-white/12 bg-white/5 p-1">
            {TABS.map(({ id, label, Icon }) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={[
                    "inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-xs font-semibold transition",
                    active ? "bg-white text-black" : "text-white/75 hover:bg-white/10",
                  ].join(" ")}
                >
                  <Icon className={active ? "h-4 w-4" : "h-4 w-4 opacity-80"} />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
            })}
          </div>

          {/* Language (disabled on notes tab) */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-white/55 font-semibold tracking-wide">Language</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-white/5 text-white/85 text-xs px-3 py-2 rounded-2xl border border-white/12 focus:outline-none focus:border-white/20"
              disabled={readOnly || tab === "notes"}
            >
              <option className="hover:text-white hover:bg-gray-600 text-black" value="typescript">Ts</option>
              <option className="hover:text-white hover:bg-gray-600 text-black" value="javascript">Js</option>
              <option className="hover:text-white hover:bg-gray-600 text-black" value="python">Py</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isTooLong && (
            <span className="text-xs text-rose-200 flex items-center gap-1.5 border border-rose-300/25 bg-rose-300/10 px-2.5 py-1 rounded-full">
              <AlertCircle className="w-3 h-3" /> Too large
            </span>
          )}

          <button
            onClick={onFormat}
            disabled={readOnly || running || tab === "notes"}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-white/12 bg-white/5 hover:bg-white/8 disabled:opacity-50 transition text-xs font-semibold text-white/85"
            title="Format code"
          >
            <Wand2 className="w-4 h-4" />
            Format
          </button>

          <button
            onClick={onReset}
            disabled={readOnly || running}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-white/12 bg-white/5 hover:bg-white/8 disabled:opacity-50 transition text-xs font-semibold text-white/85"
            title="Reset (with confirmation)"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>

          <button
            onClick={onRun}
            disabled={readOnly || running || tab === "notes" || isTooLong}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-white/12 bg-white/5 hover:bg-white/8 disabled:opacity-50 transition text-xs font-semibold text-white/90"
            title="Run (Ctrl+Enter)"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run
          </button>

          <button
            onClick={onSubmit}
            disabled={readOnly || running || tab === "notes" || isTooLong}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white text-black hover:bg-white/90 disabled:opacity-50 transition text-xs font-semibold"
            title="Submit (Ctrl+Shift+Enter)"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
            Submit
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 relative min-h-0">
        <div className="relative flex-1 h-full overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-25 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:22px_22px]" />
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-400/10 blur-[70px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-400/10 blur-[70px]" />

          <div className="h-full">
            <CodeMirror
              key={`${language}-${tab}`}
              style={{ height: "100%" }}
              ref={cmRef}
              value={String(activeValue || "")}
              height="100%"
              theme={oneDark}
              editable={!readOnly}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: true,
                highlightActiveLineGutter: true,
                foldGutter: true,
                bracketMatching: true,
                closeBrackets: true,
                tabSize: 2,
              }}
              extensions={extensions}
              onChange={(val) => onChangeActive(val)}
              onCreateEditor={(view) => setCursor(safeLineCol(view))}
              onUpdate={(vu) => {
                if (vu?.view) setCursor(safeLineCol(vu.view));
              }}
            />
          </div>
        </div>
      </div>

      {/* Output / Tests (terminal-like) */}
      {(running || lastRun) && (
        <div className="border-t border-white/10 bg-white/[0.02]">
          <div className="px-5 py-3 flex items-center justify-between">
            <div className="text-xs font-semibold text-white/80 tracking-wide">Console</div>
            {lastRun && (
              <div className="text-[11px] text-white/55">
                {lastRun.compile_ok ? "compiled" : "compile error"}
                <span className="text-white/35"> • </span>
                {lastRun.run_ok ? "ran" : "runtime error"}
              </div>
            )}
          </div>

          <div className="px-5 pb-4">
            <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
              <div className="max-h-[160px] overflow-auto p-4 font-mono text-[12px] leading-5 text-white/80">
                {running && !lastRun ? (
                  <span className="inline-flex items-center gap-2 text-white/60">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running...
                  </span>
                ) : (
                  <>
                    {lastRun?.stdout ? (
                      <pre className="whitespace-pre-wrap m-0">{String(lastRun.stdout)}</pre>
                    ) : null}
                    {lastRun?.stderr ? (
                      <pre className="whitespace-pre-wrap m-0 text-rose-200/90">{String(lastRun.stderr)}</pre>
                    ) : null}
                    {!lastRun?.stdout && !lastRun?.stderr ? (
                      <span className="text-white/50">(no output)</span>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            {Array.isArray(lastRun?.tests) && lastRun.tests.length > 0 && (
              <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                <div className="px-4 py-2 text-[11px] font-semibold text-white/75 border-b border-white/10">
                  Tests
                </div>
                <div className="max-h-[140px] overflow-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-white/50">
                        <th className="text-left px-4 py-2 font-semibold">Name</th>
                        <th className="text-left px-4 py-2 font-semibold">Status</th>
                        <th className="text-left px-4 py-2 font-semibold">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastRun.tests.map((t, idx) => (
                        <tr key={idx} className="border-t border-white/10">
                          <td className="px-4 py-2 text-white/80 font-semibold">
                            {t?.name || `Test ${idx + 1}`}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={[
                                "inline-flex items-center gap-2 rounded-full border px-2 py-0.5",
                                t?.passed
                                  ? "border-emerald-300/25 bg-emerald-300/10 text-emerald-100"
                                  : "border-rose-300/25 bg-rose-300/10 text-rose-100",
                              ].join(" ")}
                            >
                              {t?.passed ? "PASS" : "FAIL"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-white/65">
                            {t?.error || t?.message || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className="px-5 py-3 border-t border-white/10 bg-white/[0.03] flex items-center justify-between text-[11px] text-white/60">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white/75">{tab.toUpperCase()}</span>
          <span className="h-1 w-1 rounded-full bg-white/25" />
          <span>{language.toUpperCase()}</span>
          <span className="h-1 w-1 rounded-full bg-white/25" />
          <span>
            Ln {cursor.line}, Col {cursor.col}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {running ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Running {msToClock(runningMs)}
            </span>
          ) : lastRun ? (
            <span className="inline-flex items-center gap-2">
              <span
                className={[
                  "h-2 w-2 rounded-full",
                  lastRun.run_ok ? "bg-emerald-300/80" : "bg-rose-300/80",
                ].join(" ")}
              />
              {lastRun.run_ok ? "OK" : "Failed"}
              <span className="text-white/40">•</span>
              {typeof lastRun.duration_ms === "number" ? `${lastRun.duration_ms}ms` : ""}
            </span>
          ) : (
            <span className="text-white/45">Ready</span>
          )}
        </div>
      </div>
    </div>
  );
}
